import * as React from 'react';

// Placeholder
import {
  PageGeneric as PageGenericPlaceholder,
  PageLogin as PageLoginPlaceholder,
  PageResetPassword as PageResetPasswordPlaceholder,
  PageSignup as PageSignupPlaceholder,
  PageUser as PageUserPlaceholder,
} from '@shopee-user/placeholder-ui';
import { PageLoading } from '@shopee-user/business-customization-ui';

// Components
import PageUser from '@shopee-user/page-user';

// Routes utils
import { PageName, ROUTES } from '@shopee-user/url';
import { generateAsyncRouteComponent } from '@tob/routes/utils';

// Feature toggle
import { isFeatureEnabled } from '@shopee-user/business-customization-logic';
import { Feature } from '@shopee-user/feature-toggle';
import {
  isFeatureEnabled as isStaticFeatureEnabled,
  Feature as StaticFeature,
} from '@shopee-user/static-feature-toggle';
import { isUsingSandboxEnvironment } from '@tob/shared/sandbox';

// I18n constants & utils
import {
  USER_AUTHENTICATION_COLLECTION,
  USER_404_COLLECTION,
  USER_ONE_CREDENTIAL_MIGRATION_COLLECTION,
  USER_ACCOUNT_COLLECTION,
  USER_ACCEPT_INVITATION_COLLECTION,
} from '@shopee-user/transify-collections';

// Utils
import { Env } from '@shopee-user/global';
import { config } from '@shopee-user/platform';

const IS_NON_LIVE_ENV = config.ENV !== Env.Live && config.ENV !== Env.Liveish;

// Elements
const PageShopeepayLegacyUserPassword = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "RouteShopeePayLegacyUserPassword" */ './components/RouteShopeePayLegacyUserPassword'
    ),
  Placeholder: PageUserPlaceholder,
  i18nCollections: [USER_ACCOUNT_COLLECTION],
});

const PageShopeepayLegacyLoginByPassword = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageShopeePayLegacyLoginByPassword" */ './components/RouteShopeePayLegacyLoginByPassword'
    ),
  Placeholder: PageLoginPlaceholder,
  i18nCollections: isFeatureEnabled(Feature.LoginByPasswordRedirectToShopeePay)
    ? undefined
    : [USER_AUTHENTICATION_COLLECTION],
});

const PageLoginByPassword = generateAsyncRouteComponent({
  loader: () => {
    if (isFeatureEnabled(Feature.OneCredential)) {
      if (isFeatureEnabled(Feature.OneCredentialOptional)) {
        return import(
          /* webpackChunkName: "PageLoginByPasswordOneCredentialOptional" */ '../pages/login-by-password-one-credential-optional'
        );
      } else {
        return import(
          /* webpackChunkName: "PageLoginByPasswordOneCredential" */ '../pages/login-by-password-one-credential'
        );
      }
    } else {
      return import(
        /* webpackChunkName: "PageLoginByPassword" */ '../pages/login-by-password'
      );
    }
  },
  Placeholder: PageLoginPlaceholder,
  i18nCollections: [USER_AUTHENTICATION_COLLECTION],
});

const PageLoginByOtp = generateAsyncRouteComponent({
  loader: () => {
    if (isFeatureEnabled(Feature.OneCredential)) {
      if (isFeatureEnabled(Feature.OneCredentialOptional)) {
        return import(
          /* webpackChunkName: "PageLoginByOtpOneCredentialOptional" */ '../pages/login-by-otp-one-credential-optional'
        );
      } else {
        return import(
          /* webpackChunkName: "PageLoginByOtpOneCredential" */ '../pages/login-by-otp-one-credential'
        );
      }
    } else {
      return import(
        /* webpackChunkName: "PageLoginByOTP" */ '@shopee-user/login-by-otp'
      );
    }
  },
  Placeholder: PageLoginPlaceholder,
  i18nCollections: [USER_AUTHENTICATION_COLLECTION],
});

const PageLoginByNoCostAuthentication = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageLoginByNoCostAuthentication" */ '../pages/login-by-no-cost-authentication'
    ),
  Placeholder: PageLoginPlaceholder,
  i18nCollections: [USER_AUTHENTICATION_COLLECTION],
});

const PageReloginPage = generateAsyncRouteComponent({
  loader: () =>
    import(/* webpackChunkName: "PageReloginPage" */ '../pages/relogin-page'),
  Placeholder: PageLoading,
  i18nCollections: [USER_AUTHENTICATION_COLLECTION],
});

const PageDemoAbtest = generateAsyncRouteComponent({
  loader: () =>
    import(/* webpackChunkName: "PageDemoAbtest" */ '../pages/demo-abtest'),
  Placeholder: PageLoginPlaceholder,
});

const PageLoginByToken = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageLoginByToken" */ '../pages/login-by-token'
    ),
  Placeholder: PageLoading,
  i18nCollections: [USER_AUTHENTICATION_COLLECTION],
});

const PageLoginByIvs = generateAsyncRouteComponent({
  loader: () =>
    import(/* webpackChunkName: "PageLoginByIvs" */ '../pages/login-by-ivs'),
  Placeholder: PageGenericPlaceholder,
  i18nCollections: [USER_AUTHENTICATION_COLLECTION],
});

const PageLoginByThirdPartyAuth = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageLoginByThirdPartyAuth" */ '../pages/third-party-auth'
    ),
  Placeholder: PageGenericPlaceholder,
  i18nCollections: [USER_AUTHENTICATION_COLLECTION],
});

const PageSignupByOtp = generateAsyncRouteComponent({
  loader: () => {
    if (isFeatureEnabled(Feature.OneCredential)) {
      if (isFeatureEnabled(Feature.OneCredentialOptional)) {
        return import(
          /* webpackChunkName: "PageSignupByOtpOneCredentialOptional" */ '../pages/signup-by-otp-one-credential-optional'
        );
      } else {
        return import(
          /* webpackChunkName: "PageSignupByOtpOneCredential" */ '../pages/signup-by-otp-one-credential'
        );
      }
    } else {
      return import(
        /* webpackChunkName: "PageSignupByOTP" */ '@shopee-user/signup-by-otp'
      );
    }
  },
  Placeholder: PageSignupPlaceholder,
  i18nCollections: [USER_AUTHENTICATION_COLLECTION],
});

const PageResetPassword = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageResetPassword" */ '../pages/reset-password'
    ),
  Placeholder: PageResetPasswordPlaceholder,
  i18nCollections: [USER_AUTHENTICATION_COLLECTION],
});

const PageResetPasswordPinVerification = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageResetPasswordPinVerification" */ '../pages/reset-password-pin-verification'
    ),
  Placeholder: PageResetPasswordPlaceholder,
  i18nCollections: [USER_AUTHENTICATION_COLLECTION],
});

const HomePage = generateAsyncRouteComponent({
  loader: () => import(/* webpackChunkName: "Homepage" */ '../pages/homepage'),
  Placeholder: PageGenericPlaceholder,
});

const PageVerifyOtp = generateAsyncRouteComponent({
  loader: () =>
    import(/* webpackChunkName: "PageVerifyOtp" */ '../pages/verify-otp'),
  Placeholder: PageLoginPlaceholder,
  i18nCollections: [USER_AUTHENTICATION_COLLECTION],
});

const PageVerifyPassword = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageVerifyPassword" */ '../pages/verify-password'
    ),
  Placeholder: PageLoginPlaceholder,
  i18nCollections: [USER_AUTHENTICATION_COLLECTION],
});

const PageAcceptInvitation = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageAcceptInvitation" */ '../pages/one-credential-accept-invitation'
    ),
  Placeholder: PageGenericPlaceholder,
  i18nCollections: [USER_ACCEPT_INVITATION_COLLECTION],
});

const PageOneCredentialMigration = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageAccountOneCredentialMigration" */ '@shopee-user/account-one-credential-migration'
    ),
  Placeholder: PageGenericPlaceholder,
  i18nCollections: [USER_ONE_CREDENTIAL_MIGRATION_COLLECTION],
});

const PageOneCredentialSwitchToCLink = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageOneCredentialSwitchToCLink" */ '@tob/pages/account-one-credential-switch-toc-link'
    ),
});

const PageOneCredentialDelink = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageOneCredentialDelink" */ '../pages/one-credential-delink'
    ),
});

const PageUserCredentialsCommit = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageUserCredentialsCommit" */ '../pages/user-credentials-commit'
    ),
});

const PageLoginByTokenTobOneCredential = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageLoginByTokenTobOneCredential" */ '../pages/login-by-token-tob-one-credential'
    ),
});

const CrashPageTest = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "CrashPageTest" */ './components/CrashPageTest'
    ),
  Placeholder: PageGenericPlaceholder,
});

const OtpDemoPage = generateAsyncRouteComponent({
  loader: () =>
    import(/* webpackChunkName: "OtpDemoPage" */ '../pages/otp-demo-page'),
  Placeholder: PageGenericPlaceholder,
});

const ToolsPage = generateAsyncRouteComponent({
  loader: () => import(/* webpackChunkName: "ToolsPage" */ '../pages/tools'),
  Placeholder: PageGenericPlaceholder,
});

const Page404 = generateAsyncRouteComponent({
  loader: () => import(/* webpackChunkName: "Page404" */ '../pages/page-404'),
  Placeholder: PageGenericPlaceholder,
  i18nCollections: [USER_404_COLLECTION],
});

const SandboxConfigPage = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "SandboxConfigPage" */ './components/SandboxConfigPage'
    ),
  Placeholder: PageGenericPlaceholder,
});

const GtmTestPage = generateAsyncRouteComponent({
  loader: () =>
    import(/* webpackChunkName: "GtmTestPage" */ '../pages/gtm-test'),
  Placeholder: PageGenericPlaceholder,
});

const PageIvsSharedService = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageIvsSharedService" */ '../pages/ivs-shared-service'
    ),
  Placeholder: PageGenericPlaceholder,
});

const PageThirdPartyBindSharedService = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageThirdPartyBindSharedService" */ '../pages/third-party-bind-service'
    ),
  Placeholder: PageGenericPlaceholder,
});

const PageThirdPartyAuth = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageThirdPartyAuth" */ '../pages/third-party-service'
    ),
  Placeholder: PageGenericPlaceholder,
  i18nCollections: [USER_AUTHENTICATION_COLLECTION],
});

const PagePostAuthenticationService = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PagePostAuthenticationService" */ '../pages/post-authentication-service'
    ),
});

const PageNoCostAuthActivationSharedService = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageNoCostAuthActivationSharedService" */ '../pages/no-cost-auth-activation-shared-service'
    ),
  Placeholder: PageLoading,
  i18nCollections: [USER_AUTHENTICATION_COLLECTION],
});

const PageLogoutSharedService = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageLogoutSharedService" */ '../pages/logout-shared-service'
    ),
  Placeholder: PageLoading,
});

const PageDisableReloginInfo = generateAsyncRouteComponent({
  loader: () =>
    import(
      /* webpackChunkName: "PageDisableReloginInfo" */ '../pages/disable-relogin-info'
    ),
  Placeholder: PageLoading,
  i18nCollections: [USER_AUTHENTICATION_COLLECTION],
});

const FINAL_ROUTES: {
  path: string;
  name: string;
  featureToggle?: boolean;
  element: React.ReactNode;
}[] = [
  {
    path: ROUTES.SHOPEE_PAY_LEGACY_USER_PASSWORD,
    name: PageName.PageUserPassword,
    featureToggle: isFeatureEnabled(Feature.ShopeePayChangePassword),
    element: <PageShopeepayLegacyUserPassword />,
  },
  {
    path: ROUTES.SHOPEE_PAY_LEGACY_LOGIN_BY_PASSWORD,
    name: PageName.PageLoginByPassword,
    featureToggle:
      isFeatureEnabled(Feature.ShopeePayLoginByPassword) ||
      isFeatureEnabled(Feature.LoginByPasswordRedirectToShopeePay),
    element: <PageShopeepayLegacyLoginByPassword />,
  },
  {
    path: ROUTES.LOGIN_BY_PASSWORD,
    name: PageName.PageLoginByPassword,
    featureToggle: isFeatureEnabled(Feature.LoginByPasswordEntrance),
    element: <PageLoginByPassword />,
  },
  {
    path: ROUTES.LOGIN_BY_OTP,
    name: PageName.PageLoginByOTP,
    featureToggle: isFeatureEnabled(Feature.LoginByOtpEntrance),
    element: <PageLoginByOtp />,
  },
  {
    path: ROUTES.LOGIN_BY_TOKEN,
    name: PageName.PageLoginByToken,
    featureToggle: isFeatureEnabled(Feature.OneCredential),
    element: <PageLoginByToken />,
  },
  {
    path: ROUTES.LOGIN_BY_NO_COST_AUTHENTICATION,
    name: PageName.PageLoginByNoCostAuthentication,
    featureToggle:
      !isFeatureEnabled(Feature.ReloginPageEntrance) &&
      (isFeatureEnabled(Feature.BiometricsV1) ||
        isFeatureEnabled(Feature.QuickLogin)),
    element: <PageLoginByNoCostAuthentication />,
  },
  {
    path: ROUTES.RELOGIN_PAGE,
    name: PageName.PageReloginPage,
    featureToggle: isFeatureEnabled(Feature.ReloginPageEntrance),
    element: <PageReloginPage />,
  },
  {
    path: ROUTES.LOGIN_BY_IVS,
    name: PageName.PageLoginByIvs,
    featureToggle: isFeatureEnabled(Feature.IvsIntegration),
    element: <PageLoginByIvs />,
  },
  {
    path: ROUTES.THIRD_PARTY_AUTHENTICATION_SERVICE,
    name: PageName.PageThirdPartyAuth,
    element: <PageThirdPartyAuth />,
  },
  {
    path: ROUTES.LOGIN_BY_THIRD_PARTY,
    name: PageName.PageLoginByThirdParty,
    element: <PageLoginByThirdPartyAuth />,
  },
  {
    path: ROUTES.IVS_SHARED_SERVICE,
    name: PageName.PageIvsSharedService,
    featureToggle: isFeatureEnabled(Feature.IvsSharedService),
    element: <PageIvsSharedService />,
  },
  {
    path: ROUTES.THIRD_PARTY_BINDING_SHARED_SERVICE,
    name: PageName.PageThirdPartyBindSharedService,
    featureToggle: isFeatureEnabled(
      Feature.ThirdPartyBindingSharedServiceEntrance
    ),
    element: <PageThirdPartyBindSharedService />,
  },
  {
    path: ROUTES.POST_AUTHENTICATION_SERVICE,
    name: PageName.PagePostAuthenticationService,
    element: <PagePostAuthenticationService />,
  },
  {
    path: ROUTES.NO_COST_AUTH_ACTIVATION_SHARED_SERVICE,
    name: PageName.PageNoCostAuthActivationSharedService,
    element: <PageNoCostAuthActivationSharedService />,
  },
  {
    path: ROUTES.LOGOUT_SHARED_SERVICE,
    name: PageName.PageLogoutSharedService,
    element: <PageLogoutSharedService />,
  },
  {
    path: ROUTES.DISABLE_RELOGIN_INFO,
    name: PageName.PageDisableReloginInfo,
    featureToggle: isFeatureEnabled(Feature.DisableReloginInfoEntrance),
    element: <PageDisableReloginInfo />,
  },
  {
    path: ROUTES.SIGNUP_BY_OTP,
    name: PageName.PageSignupByOTP,
    featureToggle: isFeatureEnabled(Feature.SignupByOtpEntrance),
    element: <PageSignupByOtp />,
  },
  {
    path: ROUTES.RESET_PASSWORD,
    name: PageName.PageResetPassword,
    featureToggle:
      isFeatureEnabled(Feature.ResetPasswordEntrance) &&
      isStaticFeatureEnabled(StaticFeature.ResetPasswordEntrance, false),
    element: <PageResetPassword />,
  },
  {
    path: ROUTES.RESET_PASSWORD_FROM_LINK,
    name: PageName.PageResetPassword,
    featureToggle: isFeatureEnabled(Feature.ResetPasswordFromLinkEntrance),
    element: <PageResetPassword />,
  },
  {
    path: ROUTES.RESET_PASSWORD_FROM_PIN_VERIFICATION,
    name: PageName.PageResetPassword,
    featureToggle: isFeatureEnabled(Feature.ResetPasswordFromPinVerification),
    element: <PageResetPasswordPinVerification />,
  },
  {
    path: ROUTES.VERIFY_OTP,
    name: PageName.PageVerifyOtp,
    featureToggle: isFeatureEnabled(Feature.VerifyOtpSharedService),
    element: <PageVerifyOtp />,
  },
  {
    path: ROUTES.VERIFY_PASSWORD,
    name: PageName.PageVerifyPassword,
    featureToggle: isFeatureEnabled(Feature.VerifyPasswordSharedService),
    element: <PageVerifyPassword />,
  },
  {
    path: ROUTES.ACCEPT_INVITATION,
    name: PageName.PageAcceptInvitation,
    featureToggle: isFeatureEnabled(Feature.OneCredentialAcceptInvitation),
    element: <PageAcceptInvitation />,
  },
  {
    path: ROUTES.ACCOUNT_ONE_CREDENTIAL_MIGRATION,
    name: PageName.PageAccountOneCredentialMigration,
    featureToggle:
      isFeatureEnabled(Feature.OneCredential) &&
      isFeatureEnabled(Feature.OneCredentialMigrationEntrance),
    element: <PageOneCredentialMigration />,
  },
  {
    path: ROUTES.ACCOUNT_ONE_CREDENTIAL_DELINK,
    name: PageName.PageAccountOneCredentialDelink,
    featureToggle: isFeatureEnabled(Feature.OneCredentialDelinkEntrance),
    element: <PageOneCredentialDelink />,
  },
  {
    path: ROUTES.ACCOUNT_ONE_CREDENTIAL_SWITCH_TOC_LINK,
    name: PageName.PageAccountOneCredentialSwitchToCLink,
    featureToggle:
      isFeatureEnabled(Feature.OneCredentialSwitchToCLinkAdminEnabled) ||
      isFeatureEnabled(Feature.OneCredentialSwitchToCLinkStaffEnabled),
    element: <PageOneCredentialSwitchToCLink />,
  },
  {
    path: ROUTES.USER_CREDENTIALS_COMMIT,
    name: PageName.PageUserCredentialsCommit,
    featureToggle:
      isFeatureEnabled(Feature.OneCredential) &&
      isFeatureEnabled(Feature.UserCredentialsCommit),
    element: <PageUserCredentialsCommit />,
  },
  {
    path: ROUTES.LOGIN_BY_TOKEN_TOB_ONE_CREDENTIAL,
    name: PageName.PageLoginByTokenTobOneCredential,
    featureToggle: isFeatureEnabled(Feature.TobOneCredential),
    element: <PageLoginByTokenTobOneCredential />,
  },
  {
    path: ROUTES.CONFIG,
    name: 'Sandbox Config Page',
    featureToggle: isUsingSandboxEnvironment(),
    element: <SandboxConfigPage />,
  },
  // Dev routes starts
  // Those are strictly dev routes, do not enable in live environment
  {
    path: '/test/crash',
    name: 'CrashPageTest',
    featureToggle: IS_NON_LIVE_ENV,
    element: <CrashPageTest />,
  },
  {
    path: '/test/otp',
    name: 'OTPDemoPage',
    featureToggle: IS_NON_LIVE_ENV,
    element: <OtpDemoPage />,
  },
  {
    path: '/test/abtest',
    name: 'DemoAbtest',
    featureToggle: IS_NON_LIVE_ENV,
    element: <PageDemoAbtest />,
  },
  {
    path: '/test/gtm',
    name: 'GtmTestPage',
    featureToggle: IS_NON_LIVE_ENV,
    element: <GtmTestPage />,
  },
  {
    path: '/__tools*',
    name: 'ToolsPage',
    featureToggle: IS_NON_LIVE_ENV,
    element: <ToolsPage />,
  },
  // Dev routes ends
  {
    path: ROUTES.HOMEPAGE,
    name: PageName.Homepage,
    featureToggle: isFeatureEnabled(Feature.HomepageRedirection),
    element: <HomePage />,
  },
  {
    path: `${ROUTES.USER}/*`,
    name: PageName.PageUser,
    element: <PageUser />,
  },
  {
    path: '*',
    name: PageName.Page404,
    element: <Page404 />,
  },
];

export default FINAL_ROUTES.filter(routes => routes?.featureToggle !== false);
